import { apiClient } from './configs/axios-setup'

export interface ConvergenceTeamScope {
  viewAllTeams: boolean
  teams: string[]
}

export interface ConvergenceProjectRunRequest {
  teamScope?: ConvergenceTeamScope
  userId?: string
  car?: string
  area?: string
}

export interface ConvergenceCaseRequest {
  gmIds?: Array<string | number>
  states?: string[]
  project?: string
  run?: string
  teamScope?: ConvergenceTeamScope
}

export interface ConvergenceMonitorTraceRequest extends ConvergenceCaseRequest {
  monitorName?: string
  limit?: number
}

export interface ConvergenceLiveChartsRequest extends ConvergenceMonitorTraceRequest {
  includeFullTrace?: boolean
  includeTrendProjection?: boolean
}

export interface ConvergenceDashboardRequest extends ConvergenceMonitorTraceRequest {}

export interface ConvergenceTreeNode {
  key: string
  label: string
  name?: string
  desc?: string
  children?: ConvergenceTreeNode[]
}

export interface ConvergenceMetricItem {
  label: string
  value: string
}

export interface ConvergenceInvestigationSummary {
  ruleNumber: string | number
  exportedAt: string
  runCount: number
  iterations: string | number
}

export interface ConvergenceFullTraceDatum {
  iteration: number
  linearMean: number
  value: number
  trendBandUpper: number
  trendBandLower: number
}

export interface ConvergenceTrendProjectionDatum {
  iteration: number
  value: number
  trendX10: number
  toleranceUpper: number | null
  toleranceLower: number | null
  proposed190: number | null
}

export interface ConvergencePostZoomDatum {
  sample: number
  value: number
  localSampled: number
  iteration?: number
  residualP05?: number | null
  residualP95?: number | null
}

export interface ConvergenceDashboardResponse {
  projectRunNodes: ConvergenceTreeNode[]
  stateNodes: ConvergenceTreeNode[]
  selectedProjectRunKeys: Record<string, { checked?: boolean; partialChecked?: boolean }>
  selectedStateKeys: Record<string, { checked?: boolean; partialChecked?: boolean }>
  investigationSummary: ConvergenceInvestigationSummary
  cdMetrics: ConvergenceMetricItem[]
  clMetrics: ConvergenceMetricItem[]
  cdStatus?: 'red' | 'green' | 'unknown'
  clStatus?: 'red' | 'green' | 'unknown'
  overallStatus?: 'red' | 'green' | 'unknown'
  fullTraceData: ConvergenceFullTraceDatum[]
  trendProjectionData: ConvergenceTrendProjectionDatum[]
  postConvergenceZoomData: ConvergencePostZoomDatum[]
  queryErrors?: string[]
}

export type ConvergenceProjectRunRow = Record<string, string | number | null>
export type ConvergenceSummaryRow = Record<string, string | number | null>
export type ConvergenceTraceRow = Record<string, string | number | null>

export interface ConvergenceProjectRunResponse {
  projectRuns: ConvergenceProjectRunRow[]
}

export interface ConvergenceSummaryResponse {
  summaryRows: ConvergenceSummaryRow[]
}

export interface ConvergenceTraceResponse {
  traceRows: ConvergenceTraceRow[]
}

export interface ConvergenceLiveChartsResponse {
  fullTraceData?: ConvergenceFullTraceDatum[]
  trendProjectionData?: ConvergenceTrendProjectionDatum[]
  queryErrors?: string[]
}

export interface ConvergenceConnectionResponse {
  ok: boolean
  result: Record<string, string | number | null>
}

export const getConvergenceProjectRuns = async (
  request: ConvergenceProjectRunRequest = {}
): Promise<ConvergenceProjectRunResponse> => {
  const { data } = await apiClient.post<ConvergenceProjectRunResponse>(
    '/ConvergenceAnalysis/ProjectRuns',
    request
  )
  return data
}

export const getConvergenceDashboard = async (
  request: ConvergenceDashboardRequest = {}
): Promise<ConvergenceDashboardResponse> => {
  const { data } = await apiClient.post<ConvergenceDashboardResponse>(
    '/ConvergenceAnalysis/Dashboard',
    request
  )
  return data
}

export const getConvergenceSummary = async (
  request: ConvergenceCaseRequest = {}
): Promise<ConvergenceSummaryResponse> => {
  const { data } = await apiClient.post<ConvergenceSummaryResponse>(
    '/ConvergenceAnalysis/Summary',
    request
  )
  return data
}

export const getConvergenceMonitorTrace = async (
  request: ConvergenceMonitorTraceRequest = {}
): Promise<ConvergenceTraceResponse> => {
  const { data } = await apiClient.post<ConvergenceTraceResponse>(
    '/ConvergenceAnalysis/MonitorTrace',
    request
  )
  return data
}

export const getConvergenceLiveCharts = async (
  request: ConvergenceLiveChartsRequest = {}
): Promise<ConvergenceLiveChartsResponse> => {
  const { data } = await apiClient.post<ConvergenceLiveChartsResponse>(
    '/ConvergenceAnalysis/LiveCharts',
    request
  )
  return data
}

export const testConvergenceConnection = async (): Promise<ConvergenceConnectionResponse> => {
  const { data } = await apiClient.get<ConvergenceConnectionResponse>(
    '/ConvergenceAnalysis/TestConnection'
  )
  return data
}